import { useMemo, useState } from 'react';
import { useGetBettingGamesQuery } from './gql/getBettingGames.generated';
import { BettingGame, GameLifecycleStage } from '@Global/apollo/types';

type Props = {
  first?: number;
  lifecycle?: GameLifecycleStage[];
  hasGiftToClaim?: boolean;
  labels?: string[];
  isMyBattles?: boolean;
};

export const useGetBettingGames = ({ first = 10, lifecycle, hasGiftToClaim, labels, isMyBattles = false }: Props) => {
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, loading, error, fetchMore, refetch } = useGetBettingGamesQuery({
    variables: {
      first,
      lifecycle,
      hasGiftToClaim,
      labels,
      ...(isMyBattles && { myOwn: true }),
    },
  });

  const fetchNextPage = async () => {
    if (!data?.bettingGames.pageInfo.hasNextPage) return;
    try {
      setLoadingMore(true);
      await fetchMore({
        variables: {
          first,
          after: data.bettingGames.pageInfo.endCursor,
          lifecycle,
          hasGiftToClaim,
          labels,
          ...(isMyBattles && { myOwn: true }),
        },
      });
    } catch (error) {
      console.error('Error fetching more battles:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const bettingGames = useMemo(() => {
    return data?.bettingGames.edges.map((edge) => edge.node) ?? [];
  }, [data]);

  const refresh = () => {
    return refetch().catch((err) => {
      console.error('Error refreshing battles:', err);
    });
  };

  return {
    bettingGames: bettingGames as BettingGame[],
    loading,
    error,
    hasNextPage: data?.bettingGames.pageInfo.hasNextPage ?? false,
    fetchNextPage,
    refresh,
    loadingMore,
  };
};
